import type { SimulationEventType } from '../game/types'

export type AudioCue = Extract<
  SimulationEventType,
  | 'fire'
  | 'hit'
  | 'driver-hit'
  | 'reload-start'
  | 'reload-complete'
  | 'vehicle-contact'
  | 'drift-bank'
  | 'boost'
  | 'lap'
  | 'finish'
  | 'reset'
>

export type AudioCategory = 'engine' | 'weapon' | 'impact' | 'feedback'

export interface AudioAssetDefinition {
  sources: readonly [string, ...string[]]
  category: AudioCategory
  gain: number
  loop: boolean
}

export const AUDIO_ASSETS = {
  engineLow: {
    sources: ['/audio/sfx/engine-low.ogg', '/audio/sfx/engine-low.mp3'],
    category: 'engine',
    gain: 0.34,
    loop: true,
  },
  engineHigh: {
    sources: ['/audio/sfx/engine-high.ogg', '/audio/sfx/engine-high.mp3'],
    category: 'engine',
    gain: 0.34,
    loop: true,
  },
  engineBoost: {
    sources: ['/audio/sfx/engine-boost.ogg', '/audio/sfx/engine-boost.mp3'],
    category: 'engine',
    gain: 0.23,
    loop: true,
  },
  exhaustLoad: {
    sources: ['/audio/sfx/exhaust-load.ogg', '/audio/sfx/exhaust-load.mp3'],
    category: 'engine',
    gain: 0.2,
    loop: true,
  },
  driftLoop: {
    sources: ['/audio/sfx/drift-loop.ogg', '/audio/sfx/drift-loop.mp3'],
    category: 'engine',
    gain: 0.3,
    loop: true,
  },
  pistolFire01: {
    sources: ['/audio/sfx/pistol-fire-01.ogg', '/audio/sfx/pistol-fire-01.mp3'],
    category: 'weapon',
    gain: 0.3,
    loop: false,
  },
  pistolFire02: {
    sources: ['/audio/sfx/pistol-fire-02.ogg', '/audio/sfx/pistol-fire-02.mp3'],
    category: 'weapon',
    gain: 0.3,
    loop: false,
  },
  bulletImpact01: {
    sources: ['/audio/sfx/bullet-impact-01.ogg', '/audio/sfx/bullet-impact-01.mp3'],
    category: 'impact',
    gain: 0.3,
    loop: false,
  },
  bulletImpact02: {
    sources: ['/audio/sfx/bullet-impact-02.ogg', '/audio/sfx/bullet-impact-02.mp3'],
    category: 'impact',
    gain: 0.3,
    loop: false,
  },
  driverStun: {
    sources: ['/audio/sfx/driver-stun.ogg', '/audio/sfx/driver-stun.mp3'],
    category: 'impact',
    gain: 0.36,
    loop: false,
  },
  reloadStart: {
    sources: ['/audio/sfx/reload-start.ogg', '/audio/sfx/reload-start.mp3'],
    category: 'weapon',
    gain: 0.23,
    loop: false,
  },
  reloadComplete: {
    sources: ['/audio/sfx/reload-complete.ogg', '/audio/sfx/reload-complete.mp3'],
    category: 'weapon',
    gain: 0.24,
    loop: false,
  },
  vehicleContact01: {
    sources: ['/audio/sfx/vehicle-contact-01.ogg', '/audio/sfx/vehicle-contact-01.mp3'],
    category: 'impact',
    gain: 0.34,
    loop: false,
  },
  vehicleContact02: {
    sources: ['/audio/sfx/vehicle-contact-02.ogg', '/audio/sfx/vehicle-contact-02.mp3'],
    category: 'impact',
    gain: 0.34,
    loop: false,
  },
  vehicleContact03: {
    sources: ['/audio/sfx/vehicle-contact-03.ogg', '/audio/sfx/vehicle-contact-03.mp3'],
    category: 'impact',
    gain: 0.34,
    loop: false,
  },
  driftBank: {
    sources: ['/audio/sfx/drift-bank.ogg', '/audio/sfx/drift-bank.mp3'],
    category: 'feedback',
    gain: 0.24,
    loop: false,
  },
  boostStart: {
    sources: ['/audio/sfx/boost-start.ogg', '/audio/sfx/boost-start.mp3'],
    category: 'feedback',
    gain: 0.27,
    loop: false,
  },
  lap: {
    sources: ['/audio/sfx/lap.ogg', '/audio/sfx/lap.mp3'],
    category: 'feedback',
    gain: 0.25,
    loop: false,
  },
  finish: {
    sources: ['/audio/sfx/finish.ogg', '/audio/sfx/finish.mp3'],
    category: 'feedback',
    gain: 0.28,
    loop: false,
  },
  reset: {
    sources: ['/audio/sfx/reset.ogg', '/audio/sfx/reset.mp3'],
    category: 'feedback',
    gain: 0.24,
    loop: false,
  },
} as const satisfies Record<string, AudioAssetDefinition>

export type AudioSampleId = keyof typeof AUDIO_ASSETS

export const ENGINE_SAMPLE_IDS = {
  low: 'engineLow',
  high: 'engineHigh',
  boost: 'engineBoost',
  exhaust: 'exhaustLoad',
  drift: 'driftLoop',
} as const satisfies Record<string, AudioSampleId>

export const CUE_SAMPLE_IDS: Record<AudioCue, readonly AudioSampleId[]> = {
  fire: ['pistolFire01', 'pistolFire02'],
  hit: ['bulletImpact01', 'bulletImpact02'],
  'driver-hit': ['driverStun'],
  'reload-start': ['reloadStart'],
  'reload-complete': ['reloadComplete'],
  'vehicle-contact': ['vehicleContact01', 'vehicleContact02', 'vehicleContact03'],
  'drift-bank': ['driftBank'],
  boost: ['boostStart'],
  lap: ['lap'],
  finish: ['finish'],
  reset: ['reset'],
}
